"use client";

import {
  Box,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Transition,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useTimeout } from "@mantine/hooks";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import {
  IconCheck,
  IconClock,
  IconCoins,
  IconCreditCard,
  IconSparkles,
  IconTag,
} from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useAction } from "next-safe-action/hooks";
import { usePostHog } from "posthog-js/react";
import { useCallback, useMemo, useState } from "react";
import { CreditCostAccordion } from "@/components/CreditCostAccordion";
import { AlertWarning } from "@/components/ui/Alert";
import { AppConfig } from "@/config";
import { showErrorNotification } from "@/helpers/notifications";
import { useCredits } from "@/helpers/use-credits";
import { useSubscriptionDiscount } from "@/helpers/use-subscription";
import { usePaywallViewed } from "@/hooks/use-paywall-analytics";
import {
  ANALYTICS_EVENTS,
  type CheckoutStartedProps,
  type PaywallCtaClickedProps,
  type PaywallTrigger,
} from "@/lib/analytics/events";
import { getStripe } from "@/lib/stripe/client";
import {
  calculateDiscountedPriceCents,
  calculateDiscountedPriceDollars,
} from "@/lib/utils/pricing";
import { createExtraCreditsCheckout } from "@/server/actions/store";
import { CheckoutButton } from "./CheckoutButton";
import classes from "./TopUpCredits.module.css";

const { creditsPerPack, minimumOrderDollars } = AppConfig.credits.purchase;

interface TopUpCreditsFormValues {
  credits: number;
}

interface TopUpCreditsModalProps {
  opened: boolean;
  onClose: () => void;
  /** Which prompt opened the modal, for `paywall_viewed` / `checkout_started`. */
  trigger: PaywallTrigger;
}

type ModalPhase = "selection" | "checkout" | "success";

export function TopUpCreditsModal({
  opened,
  onClose,
  trigger,
}: TopUpCreditsModalProps) {
  const t = useTranslations("app.dashboard.account.TopUpCredits");
  const posthog = usePostHog();
  const [clientSecret, setClientSecret] = useState("");
  const [phase, setPhase] = useState<ModalPhase>("selection");
  const [purchasedCredits, setPurchasedCredits] = useState(0);
  const [canCloseSuccess, setCanCloseSuccess] = useState(false);

  const { prices } = useCredits();
  const {
    hasDiscount,
    discountRate,
    tierName,
    interval,
    upgradeHint,
    maxDiscount,
    isTrial,
  } = useSubscriptionDiscount();

  usePaywallViewed({
    trigger,
    plansShown:
      hasDiscount && upgradeHint
        ? ["credit_pack", upgradeHint.targetPriceLookupKey]
        : ["credit_pack"],
    enabled: opened,
  });

  // Get price per credit from the prices table (unitAmount is in cents)
  const creditPrice = prices.find(
    (p) => p.price.lookupKey === AppConfig.stripe.unifiedCreditLookupKey,
  );
  const pricePerCreditCents = creditPrice?.price.unitAmount ?? 0;

  const form = useForm<TopUpCreditsFormValues>({
    initialValues: {
      credits: creditsPerPack,
    },
    validate: {
      credits: (value) => {
        if (value <= 0) {
          return t("errors.creditsNegative");
        }
        return null;
      },
    },
  });

  // Calculate prices - apply discount to total to avoid per-credit rounding errors
  const originalTotalCents = form.values.credits * pricePerCreditCents;
  const discountedTotalCents = calculateDiscountedPriceCents(
    originalTotalCents,
    hasDiscount ? discountRate : 0,
  );
  const originalTotalPrice = originalTotalCents / 100;
  const totalPrice = discountedTotalCents / 100;
  const savings = originalTotalPrice - totalPrice;

  // Server action for creating embedded checkout
  const createCheckoutAction = useAction(createExtraCreditsCheckout, {
    onSuccess: ({ data }) => {
      if (data) {
        posthog?.capture(ANALYTICS_EVENTS.checkoutStarted, {
          purchase_type: "credits",
          plan_tier: null,
          billing_interval: null,
          price_usd: totalPrice,
          trigger,
        } satisfies CheckoutStartedProps);
        setClientSecret(data);
        setPhase("checkout");
      }
    },
    onError: ({ error }) => {
      showErrorNotification({
        title: t("errors.couldNotCreate"),
        message: error.serverError ?? t("errors.tryAgain"),
      });
    },
  });

  // Handle checkout completion (called by Stripe)
  const handleCheckoutComplete = useCallback(() => {
    setPhase("success");
  }, []);

  // Reset and close modal
  const handleClose = useCallback(() => {
    // Don't allow closing during success countdown
    if (phase === "success" && !canCloseSuccess) {
      return;
    }
    setPhase("selection");
    setClientSecret("");
    setPurchasedCredits(0);
    setCanCloseSuccess(false);
    form.reset();
    onClose();
  }, [form, onClose, phase, canCloseSuccess]);

  // Called when success countdown completes
  const handleCountdownComplete = useCallback(() => {
    setCanCloseSuccess(true);
  }, []);

  // Checkout options for EmbeddedCheckoutProvider
  const checkoutOptions = useMemo(
    () => ({
      clientSecret,
      onComplete: handleCheckoutComplete,
    }),
    [clientSecret, handleCheckoutComplete],
  );

  const handleSubmit = async (
    values: TopUpCreditsFormValues,
    event: React.FormEvent<HTMLFormElement> | undefined,
  ) => {
    // Prevent event from bubbling to parent forms
    event?.stopPropagation();

    posthog?.capture(ANALYTICS_EVENTS.paywallCtaClicked, {
      cta: "buy_credits",
      plan_tier: null,
      billing_interval: null,
      trigger,
    } satisfies PaywallCtaClickedProps);

    if (totalPrice === 0) {
      showErrorNotification({ message: t("errors.amountZero") });
      return;
    }

    if (totalPrice < minimumOrderDollars) {
      showErrorNotification({
        title: t("errors.minimumRequired"),
        message: t("errors.minimumAmount", {
          amount: minimumOrderDollars.toFixed(2),
        }),
      });
      return;
    }

    setPurchasedCredits(values.credits);
    createCheckoutAction.execute({ credits: values.credits });
  };

  const isLoading = createCheckoutAction.isPending;

  const originalPricePerPack = (creditsPerPack * pricePerCreditCents) / 100;
  const pricePerPack = calculateDiscountedPriceDollars(
    originalPricePerPack,
    hasDiscount ? discountRate : 0,
  );
  const originalPricePerCredit = pricePerCreditCents / 100;

  // Modal title based on phase
  const modalTitle = useMemo(() => {
    if (phase === "success") {
      return (
        <Group gap="xs">
          <ThemeIcon size="lg" radius="md" variant="light" color="green">
            <IconCheck size={20} />
          </ThemeIcon>
          <Text fw={600} size="lg">
            {t("modalTitleSuccess")}
          </Text>
        </Group>
      );
    }
    if (phase === "checkout") {
      return (
        <Group gap="xs">
          <ThemeIcon size="lg" radius="md" variant="light" color="blue">
            <IconCreditCard size={20} />
          </ThemeIcon>
          <Text fw={600} size="lg">
            {t("modalTitleCheckout")}
          </Text>
        </Group>
      );
    }
    return (
      <Group gap="xs">
        <ThemeIcon size="lg" radius="md" variant="light" color="yellow">
          <IconCoins size={20} />
        </ThemeIcon>
        <Text fw={600} size="lg">
          {t("modalTitleSelection")}
        </Text>
      </Group>
    );
  }, [phase, t]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={modalTitle}
      size={phase === "checkout" ? "xl" : "lg"}
      fullScreen={phase === "checkout"}
      zIndex={1000}
      padding={phase === "checkout" ? "md" : "xl"}
      radius="lg"
      classNames={{
        header: classes.modalHeader,
        body: classes.modalBody,
      }}
    >
      {/* Success Phase */}
      {phase === "success" && (
        <SuccessView
          credits={purchasedCredits}
          onClose={handleClose}
          onCountdownComplete={handleCountdownComplete}
        />
      )}

      {/* Checkout Phase */}
      {phase === "checkout" && clientSecret && (
        <EmbeddedCheckoutProvider
          stripe={getStripe()}
          options={checkoutOptions}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      )}

      {/* Selection Phase */}
      {phase === "selection" && (
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="xl">
            {/* Subscriber Discount Banner */}
            {hasDiscount && (
              <Paper className={classes.discountBanner} p="md" radius="md">
                <Stack gap="sm">
                  <Group gap="md" wrap="nowrap">
                    <Box flex={1}>
                      <Text
                        fw={600}
                        size="sm"
                        c="white"
                        tt="uppercase"
                        lts={0.5}
                      >
                        {t("subscriberBenefit", {
                          tier: tierName
                            ? t(
                                `tierName.${tierName}` as
                                  | "tierName.standard"
                                  | "tierName.premium",
                              )
                            : "",
                          interval:
                            interval === "year"
                              ? t("intervalYearly")
                              : t("intervalMonthly"),
                        })}
                      </Text>
                      <Group gap="xs" mt={4}>
                        <Text size="xl" fw={700} c="white">
                          {t("percentOff", { rate: discountRate })}
                        </Text>
                        <Text size="sm" c="white">
                          {t("onAllTopUps")}
                        </Text>
                      </Group>
                    </Box>
                  </Group>

                  {/* Upgrade Hint inside the banner */}
                  {upgradeHint && (
                    <Group
                      gap="md"
                      justify="space-between"
                      className={classes.upgradeHintInline}
                    >
                      <Group gap="xs">
                        <IconSparkles size={16} color="white" />
                        <Text size="sm" c="white" fw={500}>
                          {t("upgradeHint", {
                            tier: t(
                              `tierName.${upgradeHint.targetTier}` as
                                | "tierName.standard"
                                | "tierName.premium",
                            ),
                            interval:
                              upgradeHint.targetInterval === "year"
                                ? t("intervalYearly")
                                : t("intervalMonthly"),
                            discount: upgradeHint.targetDiscount,
                          })}
                        </Text>
                      </Group>
                      <CheckoutButton
                        priceLookupKey={upgradeHint.targetPriceLookupKey}
                        size="sm"
                        label={t("upgrade")}
                        trigger={trigger}
                      />
                    </Group>
                  )}
                </Stack>
              </Paper>
            )}

            {/* Trial User Info - discount activates after trial converts */}
            {isTrial && maxDiscount > 0 && (
              <Paper p="md" radius="md" className={classes.subscribeBanner}>
                <Group gap="md" wrap="nowrap">
                  <ThemeIcon size={40} radius="xl" variant="light" color="blue">
                    <IconSparkles size={20} />
                  </ThemeIcon>
                  <Box flex={1}>
                    <Text fw={600} size="sm">
                      {t("trialDiscountInfo")}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {t("trialDiscountDesc", { maxDiscount })}
                    </Text>
                  </Box>
                </Group>
              </Paper>
            )}

            <CreditCostAccordion
              mode={hasDiscount ? "subscriptions" : "pay-as-you-go"}
            />

            {/* Non-Subscriber Encouragement */}
            {!hasDiscount && !isTrial && maxDiscount > 0 && (
              <Paper p="md" radius="md" className={classes.subscribeBanner}>
                <Group gap="md" wrap="nowrap">
                  <ThemeIcon size={40} radius="xl" variant="light" color="blue">
                    <IconSparkles size={20} />
                  </ThemeIcon>
                  <Box flex={1}>
                    <Text fw={600} size="sm">
                      {t("subscribeToSave")}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {t("subscribeToSaveDesc", { maxDiscount })}
                    </Text>
                  </Box>
                  <Button
                    component="a"
                    href="https://www.mykaraoke.video/#pricing"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="light"
                    size="sm"
                  >
                    {t("viewPlans")}
                  </Button>
                </Group>
              </Paper>
            )}

            {/* Credits Selection */}
            <Card withBorder radius="md" p="lg">
              <Stack gap="lg">
                <Group justify="space-between" align="flex-start">
                  <Box>
                    <Text
                      fw={500}
                      size="sm"
                      c="dimmed"
                      tt="uppercase"
                      lts={0.5}
                    >
                      {t("selectAmount")}
                    </Text>
                    <Text size="sm" c="dimmed" mt={4}>
                      {hasDiscount
                        ? t.rich("creditsForDiscounted", {
                            credits: creditsPerPack,
                            originalPrice: () => (
                              <Text span td="line-through" c="dimmed">
                                ${originalPricePerPack.toFixed(2)}
                              </Text>
                            ),
                            discountedPrice: () => (
                              <Text span fw={600} c="green">
                                ${pricePerPack.toFixed(2)}
                              </Text>
                            ),
                          })
                        : t("creditsFor", {
                            credits: creditsPerPack,
                            price: `$${pricePerPack.toFixed(2)}`,
                          })}
                    </Text>
                  </Box>
                  <NumberInput
                    {...form.getInputProps("credits")}
                    step={creditsPerPack / 2}
                    min={creditsPerPack}
                    max={100000}
                    hideControls={false}
                    disabled={isLoading}
                    size="xl"
                    w={180}
                    styles={{
                      input: {
                        textAlign: "center",
                        fontWeight: 700,
                        fontSize: "1.5rem",
                      },
                    }}
                    stepHoldDelay={500}
                    stepHoldInterval={100}
                  />
                </Group>
              </Stack>
            </Card>

            {/* Price Breakdown */}
            <Transition mounted={totalPrice > 0} transition="fade">
              {(styles) => (
                <Card
                  withBorder
                  radius="md"
                  p="lg"
                  style={styles}
                  className={classes.priceCard}
                >
                  <Stack gap="md">
                    <Text
                      fw={500}
                      size="sm"
                      c="dimmed"
                      tt="uppercase"
                      lts={0.5}
                    >
                      {t("orderSummary")}
                    </Text>

                    <Stack gap="xs">
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          {t("creditsTimesPrice", {
                            credits: form.values.credits,
                            price: originalPricePerCredit.toFixed(2),
                          })}
                        </Text>
                        <Text size="sm">${originalTotalPrice.toFixed(2)}</Text>
                      </Group>

                      {hasDiscount && savings > 0 && (
                        <Group justify="space-between">
                          <Group gap="xs">
                            <IconTag
                              size={14}
                              className={classes.savingsIcon}
                            />
                            <Text size="sm" c="green" fw={500}>
                              {t("subscriberDiscount", { rate: discountRate })}
                            </Text>
                          </Group>
                          <Text size="sm" c="green" fw={500}>
                            -${savings.toFixed(2)}
                          </Text>
                        </Group>
                      )}
                    </Stack>

                    <Box className={classes.totalDivider} />

                    <Group justify="space-between" align="flex-end">
                      <Text size="sm" fw={500}>
                        {t("total")}
                      </Text>
                      <Text size="xl" fw={700} className={classes.totalPrice}>
                        ${totalPrice.toFixed(2)}
                      </Text>
                    </Group>
                  </Stack>
                </Card>
              )}
            </Transition>

            {/* Minimum Order Warning */}
            <Transition
              mounted={totalPrice > 0 && totalPrice < minimumOrderDollars}
              transition="slide-down"
            >
              {(styles) => (
                <AlertWarning style={styles}>
                  {t("minimumOrder", { amount: minimumOrderDollars })}
                </AlertWarning>
              )}
            </Transition>

            {/* Info Cards */}
            <SimpleGrid cols={2} spacing="sm">
              <Paper p="sm" radius="md" className={classes.infoCard}>
                <Group gap="xs" wrap="nowrap">
                  <ThemeIcon size="md" variant="light" color="blue" radius="xl">
                    <IconClock size={16} />
                  </ThemeIcon>
                  <Text size="sm" c="dimmed">
                    {t("validFor", {
                      years: AppConfig.credits.purchase.validityYears,
                    })}
                  </Text>
                </Group>
              </Paper>
              <Paper p="sm" radius="md" className={classes.infoCard}>
                <Group gap="xs" wrap="nowrap">
                  <ThemeIcon size="md" variant="light" color="blue" radius="xl">
                    <IconCreditCard size={16} />
                  </ThemeIcon>
                  <Text size="sm" c="dimmed">
                    {t("minOrder", { amount: minimumOrderDollars })}
                  </Text>
                </Group>
              </Paper>
            </SimpleGrid>

            {/* Actions */}
            <Group justify="flex-end" mt="md">
              <Button
                variant="subtle"
                onClick={handleClose}
                disabled={isLoading}
                size="md"
                color="gray"
              >
                {t("cancel")}
              </Button>
              <Button
                type="submit"
                size="lg"
                disabled={totalPrice === 0 || totalPrice < minimumOrderDollars}
                loading={isLoading}
                className={classes.submitButton}
                radius="md"
              >
                {totalPrice === 0
                  ? t("selectCredits")
                  : t("pay", { amount: totalPrice.toFixed(2) })}
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  );
}

// Timeout duration in milliseconds before allowing user to close
const SUCCESS_TIMEOUT_MS = 5000;

// Success view component shown after purchase completion
function SuccessView({
  credits,
  onClose,
  onCountdownComplete,
}: {
  credits: number;
  onClose: () => void;
  onCountdownComplete: () => void;
}) {
  const t = useTranslations("app.dashboard.account.TopUpCredits");
  const [isReady, setIsReady] = useState(false);

  useTimeout(
    () => {
      setIsReady(true);
      onCountdownComplete();
    },
    SUCCESS_TIMEOUT_MS,
    { autoInvoke: true },
  );

  if (!isReady) {
    return (
      <Stack align="center" gap="lg" py="xl">
        <Loader size="xl" />
        <Title order={2}>{t("processingPurchase")}</Title>
        <Text size="sm" c="dimmed" ta="center">
          {t("pleaseWait")}
        </Text>
      </Stack>
    );
  }

  return (
    <Stack align="center" gap="lg" py="xl">
      <ThemeIcon size={80} radius="xl" color="green" variant="light">
        <IconCheck size={48} />
      </ThemeIcon>
      <Title order={2}>{t("paymentSuccessful")}</Title>
      <Text size="lg" c="dimmed" ta="center">
        {t("creditsAdded", { credits })}
      </Text>
      <Button size="lg" onClick={onClose} mt="md">
        {t("continue")}
      </Button>
    </Stack>
  );
}
